import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Check } from "lucide-react";
import { toast } from "sonner";
import { MaskLine } from "@/components/Reveal";
import api from "@/lib/api";

const TIMELINE = ["1 to 3 days", "3 to 7 days", "1 to 2 weeks", "2 to 4 weeks", "More than 4 weeks"];

const inputCls =
  "w-full rounded-md border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/15";

export default function DiscussRequirement() {
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({
    role: "", positions: "", locations: "", skills: "", details: "",
    timeline: "", name: "", company: "", work_email: "", phone: "",
  });

  const set = (k, v) => {
    setData({ ...data, [k]: v });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const errs = {};
    if (!data.role.trim()) errs.role = "Job title is required";
    if (!data.positions.trim()) errs.positions = "Number of positions is required";
    if (!data.locations.trim()) errs.locations = "Location is required";
    if (!data.timeline) errs.timeline = "Please select a timeline";
    if (!data.name.trim()) errs.name = "Name is required";
    if (!data.company.trim()) errs.company = "Company name is required";
    if (!data.work_email.trim()) errs.work_email = "Work email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.work_email)) errs.work_email = "Enter a valid work email";
    if (!data.phone.trim()) errs.phone = "Phone number is required";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/requirements", data);
      setDone(true);
    } catch {
      toast.error("Something went wrong while sending your requirement. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const Err = ({ k }) => errors[k] ? <p className="mt-2 text-xs text-brand-red" data-testid={`req-error-${k}`}>{errors[k]}</p> : null;

  return (
    <>
      <section className="pt-[120px] pb-10 lg:pt-[148px] lg:pb-12 relative overflow-hidden" data-testid="discuss-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full grid grid-cols-2 lg:grid-cols-4">
            <div className="border-l border-slate-200/60" />
            <div className="border-l border-slate-200/60" />
            <div className="border-l border-slate-200/60 hidden lg:block" />
            <div className="border-l border-r border-slate-200/60 hidden lg:block" />
          </div>
        </div>
        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <MaskLine delay={0.05}>
            <span className="inline-flex items-center gap-4">
              <span className="h-px w-10 bg-brand-red" />
              <span className="font-mono text-[11px] uppercase tracking-[0.38em] text-brand-red font-medium">For Employers</span>
              <span className="h-px w-10 bg-brand-red" />
            </span>
          </MaskLine>
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <MaskLine delay={0.18}>Tell us what <span className="text-brand-red">you need.</span></MaskLine>
          </h1>
          <MaskLine delay={0.32} className="mt-5">
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              A straightforward conversation. A clear understanding of your requirement. The right support from there.
            </p>
          </MaskLine>
        </div>
      </section>

      <section className="pb-16 lg:pb-24" data-testid="discuss-form-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="border border-slate-200 p-10 sm:p-14 text-center" data-testid="requirement-confirmation">
              <span className="inline-flex p-4 rounded-full bg-brand-light text-brand-red"><CheckCircle2 size={28} /></span>
              <h2 className="mt-6 font-display font-bold text-slate-900 text-2xl sm:text-3xl">Thank you for reaching out to Genspire.</h2>
              <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed max-w-md mx-auto">
                We've received your requirement. Our team will review the details and get in touch with you shortly.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/" data-testid="confirmation-back-home"
                  className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-brand-red text-slate-800 hover:text-brand-red font-medium text-sm px-8 py-4 transition-colors duration-300">
                  Back to Genspire
                </Link>
                <Link to="/how-genspire-works" data-testid="confirmation-how-it-works"
                  className="group inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-dark text-white font-medium text-sm px-8 py-4 transition-colors duration-300">
                  Explore Our Approach
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={submit} noValidate className="border border-slate-200 p-7 sm:p-10" data-testid="requirement-form">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400 mb-7">Your Requirement</p>
              <div className="space-y-5">
                <div>
                  <input placeholder="Job Title *" value={data.role} onChange={(e) => set("role", e.target.value)}
                    className={`${inputCls} ${errors.role ? "border-brand-red" : ""}`} data-testid="req-role-input" />
                  <Err k="role" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <input placeholder="Number of Positions *" value={data.positions} onChange={(e) => set("positions", e.target.value)}
                      className={`${inputCls} ${errors.positions ? "border-brand-red" : ""}`} data-testid="req-positions-input" />
                    <Err k="positions" />
                  </div>
                  <div>
                    <input placeholder="Location *" value={data.locations} onChange={(e) => set("locations", e.target.value)}
                      className={`${inputCls} ${errors.locations ? "border-brand-red" : ""}`} data-testid="req-locations-input" />
                    <Err k="locations" />
                  </div>
                </div>
                <input placeholder="Key Skills / Experience" value={data.skills} onChange={(e) => set("skills", e.target.value)}
                  className={inputCls} data-testid="req-skills-input" />
                <textarea rows={3} value={data.details} onChange={(e) => set("details", e.target.value)}
                  placeholder="Brief Requirement"
                  className={`${inputCls} resize-none`} data-testid="req-details-input" />
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-3">Hiring Timeline *</p>
                  <div className="flex flex-wrap gap-2.5">
                    {TIMELINE.map((t) => (
                      <button key={t} type="button" onClick={() => set("timeline", t)} data-testid={`req-timeline-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                        className={`inline-flex items-center gap-2.5 border px-5 py-3 text-sm font-medium transition-all duration-300 ${
                          data.timeline === t ? "border-brand-red bg-brand-red text-white" : "border-slate-200 bg-white text-slate-700 hover:border-brand-red/60 hover:text-brand-red"
                        }`}>
                        <span className={`flex h-4 w-4 items-center justify-center rounded-full border transition-colors ${data.timeline === t ? "border-white bg-white/20" : "border-slate-300"}`}>
                          {data.timeline === t && <Check size={11} strokeWidth={3.5} />}
                        </span>
                        {t}
                      </button>
                    ))}
                  </div>
                  <Err k="timeline" />
                </div>
              </div>

              <div className="my-8 h-px bg-slate-200" />

              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400 mb-7">Your Details</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <input placeholder="Name *" value={data.name} onChange={(e) => set("name", e.target.value)}
                    className={`${inputCls} ${errors.name ? "border-brand-red" : ""}`} data-testid="req-name-input" />
                  <Err k="name" />
                </div>
                <div>
                  <input placeholder="Company *" value={data.company} onChange={(e) => set("company", e.target.value)}
                    className={`${inputCls} ${errors.company ? "border-brand-red" : ""}`} data-testid="req-company-input" />
                  <Err k="company" />
                </div>
                <div>
                  <input type="email" placeholder="Work Email *" value={data.work_email} onChange={(e) => set("work_email", e.target.value)}
                    className={`${inputCls} ${errors.work_email ? "border-brand-red" : ""}`} data-testid="req-email-input" />
                  <Err k="work_email" />
                </div>
                <div>
                  <input placeholder="Phone *" value={data.phone} onChange={(e) => set("phone", e.target.value)}
                    className={`${inputCls} ${errors.phone ? "border-brand-red" : ""}`} data-testid="req-phone-input" />
                  <Err k="phone" />
                </div>
              </div>

              <button type="submit" disabled={loading} data-testid="req-submit-button"
                className="group mt-9 w-full inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-dark text-white font-medium text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60">
                {loading ? "Sending…" : "Send Requirement"}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
